/**
 * Amberden — global lights switch.
 *
 * Flips [data-lights="on"|"off"] on <html>. Everything visual hangs off that
 * one attribute in assets/amberden.css: the room darkens, every lamp on the
 * page cross-fades from its OFF frame to its ON frame, glows bloom, and
 * lifestyle photography dims. All on the 1.2s lighting curve.
 *
 * The choice persists, because someone who turned the lights on wants them on
 * while they browse the rest of the range.
 *
 * Also owns the decode gate — see the block above bind(). The switch waits for
 * the ON frames to be decodable before it fires, but never for longer than
 * 300ms, so the cross-fade cannot fade to an empty frame on a cold cache.
 *
 * No dependencies, no framework.
 */

(() => {
  const KEY = 'amberden:lights';
  const root = document.documentElement;

  const isOn = () => root.dataset.lights === 'on';

  const apply = (on, { persist = true } = {}) => {
    root.dataset.lights = on ? 'on' : 'off';

    for (const el of document.querySelectorAll('[data-amb-lights-toggle]')) {
      el.setAttribute('aria-checked', String(on));
    }

    /* The product hero stacks two photographs of the same lamp in one slot and
       cross-fades between them (rework/BUILD-BRIEF.md, GALLERY). Both frames are
       in the DOM at all times — that is what search engines and a no-JS visit
       need, and what makes the fade possible at all — but only one of them is
       on screen, and the alt sentences are the richest copy the store owns. Left
       alone, a screen reader would read the lamp twice, once unlit and once lit,
       and the caption twice with it. So the frame that is not showing leaves the
       accessibility tree. Opt-in by class, so nothing outside a marked pair is
       touched: the collection cards keep the behaviour they have. */
    for (const el of document.querySelectorAll('.amb-state--off')) {
      el.setAttribute('aria-hidden', String(on));
    }
    for (const el of document.querySelectorAll('.amb-state--on')) {
      el.setAttribute('aria-hidden', String(!on));
    }

    if (persist) {
      try {
        localStorage.setItem(KEY, on ? 'on' : 'off');
      } catch {
        /* Private browsing — the toggle still works, it just won't persist. */
      }
    }
  };

  // Restore before first paint where possible; snippets/amberden-lights.liquid
  // also sets the attribute inline in <head> to avoid a flash of the wrong state.
  const restore = () => {
    let stored = null;
    try {
      stored = localStorage.getItem(KEY);
    } catch {
      /* ignore */
    }
    apply(stored === 'on', { persist: false });
  };

  /* --- The decode gate ---------------------------------------------------
     Protects the one feature nothing else in this category ships.

     The cross-fade layers an OFF frame and an ON frame and transitions opacity
     between them over 1.2s. The ON frames are full-size photographs sitting at
     opacity: 0, and a browser is entitled to deprioritise or defer a fully
     transparent image. If the ON frame has not decoded when the transition
     starts, the browser fades to nothing and then pops the image in when it
     arrives — so the marquee interaction misfires on exactly the visit that
     matters, the first one.

     The global switch makes it worse than the per-card hover: it fires every
     lamp on the page at once, so a homepage with four grid tiles needs four ON
     frames decoded simultaneously the instant the visitor touches the switch.

     In Common With never has this problem because it hard-swaps pre-rendered
     markup rather than cross-fading. Amberden's cross-fade is the more refined
     treatment and it is the one that introduces the decode dependency. Keep the
     cross-fade; add the guarantee. */

  const DECODE_BUDGET_MS = 300;

  const onFrames = () => document.querySelectorAll('.amb-lamp__frame--on');

  /* Resolves when every ON frame has decoded, or when the budget expires —
     whichever comes first. A slightly imperfect fade is better than an
     unresponsive control, and the switch must never feel latent. On a warm
     cache decode() settles in the same tick, so this costs nothing. */
  const gate = () => {
    const frames = [...onFrames()];
    if (!frames.length) return Promise.resolve();

    const decoded = Promise.allSettled(
      frames.map((f) => (typeof f.decode === 'function' ? f.decode() : Promise.resolve()))
    );

    return Promise.race([
      decoded,
      new Promise((resolve) => setTimeout(resolve, DECODE_BUDGET_MS)),
    ]);
  };

  /* Below the fold, promote an ON frame the moment its card is within roughly
     one viewport, so the decode has already happened by the time anyone can
     reach the switch. fetchpriority stays low — this must never compete with
     the images actually on screen. */
  const promote = () => {
    if (!('IntersectionObserver' in window)) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          for (const frame of e.target.querySelectorAll('.amb-lamp__frame--on')) {
            frame.loading = 'eager';
            frame.fetchPriority = 'low';
          }
          io.unobserve(e.target);
        }
      },
      { rootMargin: '100% 0px' }
    );

    for (const lamp of document.querySelectorAll('.amb-lamp')) io.observe(lamp);
  };

  /* --- The press -----------------------------------------------------------
     One listener on document, in the bubble phase, resolved with closest().
     Two things this replaces, both found 2026-09-07:

     Per-element binding. bind() used to walk [data-amb-lights-toggle] once at
     load and mark each with data-amb-bound. assets/media-gallery.js replaces
     the whole <media-gallery> on a variant change (replaceWith, l.52), so on
     any product with variants the in-frame switch that arrived with the new
     gallery had no listener and nothing ever bound it — the rebinding hooks
     were shopify:section:load/select, which only fire in the theme editor.
     Delegation survives a re-render by construction.

     stopPropagation() for the in-frame instance. It was meant to keep a press
     inside the mobile carousel's slide from also reaching the slide's
     on:click="…/open/N" and opening the zoom dialog. It could not: Horizon
     registers its delegated on:* listeners with { capture: true }
     (assets/component.js l.322), so the dialog was already open by the time
     this handler ran. The fix is structural, not here — the carousel's switch
     is now rendered outside the slide (snippets/product-media-gallery-
     content.liquid) — and the guard is gone rather than left in as a comfort. */
  const press = (event) => {
    const el = event.target instanceof Element ? event.target.closest('[data-amb-lights-toggle]') : null;
    if (!el) return;

    const next = !isOn();
    // Anyone who has pressed the switch has nothing left to be shown — the
    // arrival demonstration below checks this key and stays away for good.
    try {
      localStorage.setItem('amberden:switched', '1');
    } catch {
      /* ignore */
    }
    // Turning the lights off needs no guarantee — the OFF frames are the
    // ones already on screen.
    if (!next) return apply(false);
    // The gate can hold the state flip for up to DECODE_BUDGET_MS on a cold
    // cache. That used to mean up to 300ms of a completely dead control;
    // the :active response in amberden.css §7 now fires on pointer-down and
    // covers the window regardless of how long the decode takes.
    gate().then(() => apply(true));
  };

  restore();
  document.addEventListener('click', press);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', promote, { once: true });
  } else {
    promote();
  }
})();

/* Dock the switch into the header (teardown item 08, 2026-08-21; two homes
   since 2026-09-08). As a fixed page overlay it collided with the footer bar
   on desktop and the product title on mobile — and read as a chat bubble. A
   light switch lives at the room's entrance: the header. Which part of it
   depends on the template (the owner, 2026-09-08): on every page but a
   product page the lights band under the row is home, and the switch is
   rendered inside it (snippets/amberden-lights-band.liquid) so nothing moves;
   on a product page the band is hidden and the switch is moved up into the
   row's right column, beside the utilities, where the box on the photograph
   hands off to it. The band carries --shown when it is the home. */
(function () {
  function place() {
    /* The GLOBAL switch, specifically — data-amb-lights-dock marks it. The
       :not() covers a template that renders the switch some other way. */
    var toggle =
      document.querySelector('[data-amb-lights-dock]') ||
      document.querySelector('[data-amb-lights-toggle]:not(.amb-lights--inframe)');
    if (!toggle) return;

    var column = document.querySelector('.header__column--right');
    var slot = document.querySelector('[data-amb-lights-band-slot]');

    var bandShown = !!(slot && slot.closest('.amb-lightsbar--shown'));

    if (bandShown) {
      if (toggle.parentElement !== slot) slot.appendChild(toggle);
      toggle.classList.add('amb-lights--band');
      toggle.classList.remove('amb-lights--docked');
    } else if (column) {
      /* On mobile the column is display:contents, so siblings stack on one
         grid cell — dock inside header-actions (the icons row), which lays
         out on every viewport. */
      var actions = column.querySelector('header-actions') || column;
      if (!toggle.closest('.header__column--right')) actions.insertBefore(toggle, actions.firstChild);
      toggle.classList.add('amb-lights--docked');
      toggle.classList.remove('amb-lights--band');
    } else if (slot) {
      if (toggle.parentElement !== slot) slot.appendChild(toggle);
      toggle.classList.add('amb-lights--band');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', place);
  } else {
    place();
  }
})();

/* One switch, two homes, never both (2026-09-08; 01-brand/design-preview/
   switch-lab-2-one-switch.html §2 and §6).

   The product hero carries its own instance of the switch, on the
   photograph. While that photograph is in view it is the switch, and the
   header's instance is hidden; when the photograph scrolls out the control
   climbs into the header — the band on a phone, the row on a laptop — and
   comes back when the photograph does. There is never a moment with two.

   Mechanism: an IntersectionObserver on the visible hero pair stamps
   data-amb-lights-home="frame" | "header" on <html>; amberden.css §7 shows
   the instance that matches and hides the other with visibility, so the
   hidden one leaves the accessibility tree. Three things a first draft got
   wrong, all from the review of the proposal:

   - Hysteresis. Leave the frame when less than 40% of the pair is visible,
     return only once 60% is. A single threshold flips on every crossing and a
     slow scroll near the line makes the control blink between homes.
   - Re-observe. assets/media-gallery.js replaces the whole <media-gallery> on
     a variant change (replaceWith), so an observer bound once is holding a
     detached hero and the home never changes again. A MutationObserver on the
     product section re-finds the pair.
   - Focus. If the instance that is leaving has keyboard focus, hiding it drops
     focus to <body>; the arriving one takes it instead.

   The carousel's own rule counts too: on any slide but the hero its switch is
   hidden (amberden.css §6b — the label goes with the dots), so off the hero
   slide the header is home whatever the observer says. Horizon's slideshow
   announces every slide change as slideshow:select, user-driven or not.

   Pages without a pair stamp header once and never observe. Product templates
   are stamped frame server-side (layout/theme.liquid) so the first paint is
   already right; a product with no pair is corrected here on load. */
(function () {
  var root = document.documentElement;
  var io = null;
  var hero = null;
  var ratio = 1;
  var onHeroSlide = true;

  /* The first one that is laid out: on a laptop the carousel's instance is
     display:none and comes first in the document. */
  var visible = function (selector) {
    var all = document.querySelectorAll(selector);
    for (var i = 0; i < all.length; i++) {
      if (all[i].closest('dialog')) continue;
      if (all[i].offsetParent !== null) return all[i];
    }
    return null;
  };

  var instance = function (home) {
    return home === 'frame' ? visible('.amb-lights--inframe') : document.querySelector('[data-amb-lights-dock]');
  };

  var set = function (home) {
    if (root.dataset.ambLightsHome === home) return;
    var leaving = document.activeElement;
    root.dataset.ambLightsHome = home;
    if (leaving && leaving.hasAttribute && leaving.hasAttribute('data-amb-lights-toggle')) {
      var arriving = instance(home);
      if (arriving && arriving !== leaving) arriving.focus({ preventScroll: true });
    }
  };

  var decide = function () {
    if (!hero) return set('header');
    var inView = root.dataset.ambLightsHome === 'frame' ? ratio >= 0.4 : ratio >= 0.6;
    set(inView && onHeroSlide ? 'frame' : 'header');
  };

  /* The hero pair that is actually laid out: the carousel's first slide on a
     phone, the grid's hero on a laptop — the other is display:none and would
     report 0. The pair is .amb-pair (snippets/product-media.liquid), which is
     the OFF/ON stack itself; .amb-lamp is the cards' class, not the hero's. */
  var findHero = function () {
    return visible('media-gallery .amb-pair');
  };

  var observe = function () {
    var next = findHero();
    if (next === hero && io) return;
    if (io) io.disconnect();
    hero = next;
    if (!hero || !('IntersectionObserver' in window)) return set('header');
    io = new IntersectionObserver(
      function (entries) {
        ratio = entries[entries.length - 1].intersectionRatio;
        decide();
      },
      { threshold: [0, 0.4, 0.6, 1] }
    );
    io.observe(hero);
  };

  var start = function () {
    observe();
    if (!hero) return;
    var section = hero.closest('.shopify-section') || document.body;
    if ('MutationObserver' in window) {
      new MutationObserver(observe).observe(section, { childList: true, subtree: true });
    }
    window.matchMedia('(min-width: 750px)').addEventListener('change', observe);
    document.addEventListener('slideshow:select', function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target || !target.closest('media-gallery') || target.closest('dialog')) return;
      onHeroSlide = !event.detail || event.detail.index === 0;
      decide();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* The arrival (2026-09-08; the owner's call, and the reviewer's suggestion
   for where the one unrequested motion on the site should go).

   Once per session, a beat after the page has settled: the switch that is
   showing strikes — heats and cools in 900ms, no state change (amberden.css
   §7, @keyframes amb-strike) — and 100ms later the hero pair answers, lit on
   the lighting curve, held half a second, cooled on the cooling curve. About
   two seconds. It teaches "this controls that" without a sentence, which is
   why the header line could become the store's promise instead.

   Guards, all of them hard:
   - prefers-reduced-motion: nothing.
   - anyone who has ever pressed the switch (amberden:switched, set in press)
     — the demonstration is for the person who does not know yet.
   - the lights already on (a returning visitor): nothing to show.
   - once per session (sessionStorage), set at the moment it starts.
   - the hero must be at least 60% in view and no dialog open, checked before
     the wait and again after it; if the visitor has moved on, nothing fires.
   - the hero's ON frame is decoded first (1.5s cap), so the answer cannot fade
     to an empty frame on a cold cache — the same gate the press has.

   Which switch strikes is whichever one is showing: on a product page at
   load that is the box on the lamp, so the strike and the answer share one
   frame; on the homepage it is the band's, and the first pair in view answers. */
(function () {
  var SESSION = 'amberden:arrival';
  var SWITCHED = 'amberden:switched';
  var root = document.documentElement;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var visible = function (selector) {
    var all = document.querySelectorAll(selector);
    for (var i = 0; i < all.length; i++) {
      if (all[i].closest('dialog')) continue;
      if (all[i].offsetParent !== null) return all[i];
    }
    return null;
  };

  var inView = function (el) {
    var r = el.getBoundingClientRect();
    if (r.height <= 0) return false;
    var seen = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    return seen / r.height >= 0.6;
  };

  var blocked = function () {
    try {
      if (sessionStorage.getItem(SESSION) || localStorage.getItem(SWITCHED)) return true;
    } catch (e) {
      /* storage unavailable: still runs, just not remembered */
    }
    return root.dataset.lights === 'on' || !!document.querySelector('dialog[open]');
  };

  var run = function () {
    if (blocked()) return;
    var hero = visible('media-gallery .amb-pair') || visible('.amb-lamp');
    if (!hero || !inView(hero)) return;
    var frame = hero.querySelector('.amb-lamp__frame--on');
    if (!frame) return;
    var toggle =
      root.dataset.ambLightsHome === 'frame'
        ? visible('.amb-lights--inframe')
        : document.querySelector('[data-amb-lights-dock]');
    if (!toggle) return;

    var decoded = typeof frame.decode === 'function' ? frame.decode().catch(function () {}) : Promise.resolve();
    Promise.race([decoded, new Promise(function (resolve) { setTimeout(resolve, 1500); })]).then(function () {
      if (blocked() || !inView(hero)) return;
      try {
        sessionStorage.setItem(SESSION, '1');
      } catch (e) {
        /* ignore */
      }
      toggle.classList.add('amb-lights--strike');
      toggle.addEventListener('animationend', function () { toggle.classList.remove('amb-lights--strike'); }, { once: true });
      setTimeout(function () { hero.classList.add('amb-arrival'); }, 100);
      setTimeout(function () { hero.classList.remove('amb-arrival'); }, 100 + 1200 + 500);
    });
  };

  var start = function () { setTimeout(run, 700); };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
})();
